import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";

// 2. Why This Exists — three blocks
const WHY_EXISTS = [
  {
    n: "01",
    heading: "The Gap",
    body: ["Botswana exports raw hides.", "Value is captured outside the country."],
  },
  {
    n: "02",
    heading: "The Opportunity",
    body: [
      "Establish local tanning and finished goods manufacturing.",
      "Capture processing, design, and export margins domestically.",
    ],
  },
  {
    n: "03",
    heading: "The Outcome",
    body: ["Industrial jobs.", "Export revenues.", "A scalable manufacturing base."],
  },
];

// 3. The Project — scope
const PROJECT_SCOPE = [
  "Tannery (hide processing)",
  "Finished goods manufacturing (bags, footwear, accessories)",
  "Export-oriented production platform",
];

// 4. Current Status
const CURRENT_STATUS = [
  "Project structuring phase initiated",
  "Engagement with SEZA under Lobatse SEZ framework",
  "Raw material supply pathways under discussion with Botswana Meat Commission (BMC)",
  "International technical and manufacturing partners engaged",
];

// 5. Why This Version Works
const WHY_THIS_VERSION = [
  "Private-sector led execution (not purely government-driven)",
  "Integrated model (processing + finished goods, not standalone tannery)",
  "Export-first strategy aligned to global markets",
  "International manufacturing expertise engaged from inception",
];

// 6. Why Botswana
const WHY_BOTSWANA = [
  "Established cattle base (raw material source)",
  "Stable, investment-aligned policy environment",
  "SEZA industrial framework supporting export manufacturing",
  "Strategic positioning for access to international markets",
];

// 8. Partnership Model
const PARTNERSHIP = [
  { code: "LRG", name: "LRG Holdings", role: "Project structuring and execution leadership" },
  { code: "SEZA", name: "SEZA", role: "Industrial framework and licensing engagement" },
  { code: "INT", name: "International Partners", role: "Technical expertise, manufacturing capability, product development" },
  { code: "INV", name: "Investors", role: "Capital participation at SPV level" },
];

// 9. Execution Priorities
const PRIORITIES = [
  "Secure raw material throughput framework",
  "Finalize site allocation within Lobatse SEZ",
  "Define initial production capacity and phased scale-up",
  "Align technical partners with operating model",
];

// 10. Engagement counterparties
const COUNTERPARTIES = [
  "Strategic investors",
  "Technical and manufacturing partners",
  "Institutional stakeholders",
];

export default function Overview() {
  return (
    <div data-testid="page-overview" className="bg-[#F9F9F6]">
      {/* ================= 1. HERO ================= */}
      <section data-testid="hero-section" className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1776625834115-467d0f22e499?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsZWF0aGVyJTIwdGV4dHVyZSUyMG1hY3JvfGVufDB8fHx8MTc3NzIzNDQ3Nnww&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/30 via-[#1A1A1A]/60 to-[#1A1A1A]/95" />
        <div className="absolute inset-0 grain" />

        <div className="absolute top-24 md:top-28 left-0 right-0 z-10">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.3em] text-[#E5C4A1]">
              <span className="h-px w-10 bg-[#E5C4A1]" />
              <span>LRG Holdings</span>
              <span className="text-[#A8A8A0]">/</span>
              <span className="text-[#C7C7BF]">Lobatse, Botswana</span>
              <span className="text-[#A8A8A0]">/</span>
              <span className="text-[#E5C4A1]">SEZA-Licensed (SEZBW00029)</span>
            </div>
          </div>
        </div>

        <div className="relative w-full max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pb-16 md:pb-24 pt-32">
          <h1
            data-testid="hero-headline"
            className="font-serif text-[#F9F9F6] text-[44px] sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.95] tracking-[-0.02em] rise"
            style={{ animationDelay: "0.15s" }}
          >
            Botswana Leather
            <br />
            Industrial Platform.
          </h1>

          <p
            data-testid="hero-subheading"
            className="font-serif text-[#E5C4A1] text-xl md:text-2xl lg:text-[28px] leading-snug tracking-tight mt-6 max-w-4xl rise"
            style={{ animationDelay: "0.3s" }}
          >
            Structuring a private-sector led tanning and manufacturing export base in Lobatse, Botswana.
          </p>

          <p
            data-testid="hero-body"
            className="mt-6 text-sm md:text-base text-[#C7C7BF] max-w-2xl rise"
            style={{ animationDelay: "0.45s" }}
          >
            Led by LRG Holdings in coordination with SEZA and international manufacturing partners.
          </p>

          <div
            className="mt-10 flex flex-wrap items-center gap-6 rise"
            style={{ animationDelay: "0.6s" }}
          >
            <Link
              to="/contact"
              data-testid="hero-cta-engage"
              className="inline-flex items-center gap-3 px-7 py-4 bg-[#F9F9F6] text-[#1A1A1A] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#5C2018] hover:text-[#F9F9F6] transition-colors duration-300"
            >
              Engage With LRG
              <ArrowRight size={16} />
            </Link>
            <p
              data-testid="hero-status-line"
              className="text-[11px] uppercase tracking-[0.22em] text-[#E5C4A1] border-l border-[#E5C4A1]/50 pl-4"
            >
              Phase 1 structuring now underway · engaging strategic partners and capital
            </p>
          </div>
        </div>
      </section>

      {/* ================= MACRO CREDIBILITY STRIP ================= */}
      <section data-testid="macro-stats-strip" className="bg-[#1A1A1A] text-[#F9F9F6] border-y border-[#1A1A1A]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-14">
          <div className="grid grid-cols-2 md:grid-cols-5 border-l border-t border-[#333330]">
            {[
              ["~1.7M", "National Herd"],
              ["BBB− / Baa1", "Sovereign Credit (IG)"],
              ["55+", "Years Stability"],
              ["5%", "SEZ Tax (yrs 1-10)"],
              ["35 ha", "Lobatse SEZ"],
            ].map(([v, k]) => (
              <div key={k} className="border-r border-b border-[#333330] p-5 md:p-7">
                <p className="font-serif text-3xl md:text-4xl text-[#E5C4A1] tracking-tight">{v}</p>
                <p className="eyebrow text-[#A8A8A0] mt-3">{k}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[10px] uppercase tracking-[0.22em] text-[#A8A8A0]">
            Sources · Statistics Botswana · S&amp;P Global Ratings (Mar 2026) · Moody&rsquo;s Investors Service (Oct 2025) · SEZA
          </p>
        </div>
      </section>

      {/* ================= 2. WHY THIS EXISTS ================= */}
      <section
        data-testid="why-exists-section"
        className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32"
      >
        <div className="grid grid-cols-12 gap-8 mb-14 md:mb-16">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow">01 / Why This Exists</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
              Why This Exists.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 border-l border-t border-[#1A1A1A]">
          {WHY_EXISTS.map((b) => (
            <article
              key={b.n}
              data-testid={`why-exists-${b.n}`}
              className="border-r border-b border-[#1A1A1A] p-8 md:p-10 bg-[#F9F9F6] hover:bg-white transition-colors"
            >
              <span className="font-serif text-2xl text-[#5C2018]">{b.n}</span>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight mt-8 font-semibold">
                {b.heading}
              </h3>
              <div className="mt-5 space-y-1.5">
                {b.body.map((line) => (
                  <p key={line} className="text-base md:text-lg text-[#1A1A1A] leading-snug">
                    {line}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ================= 3. THE PROJECT ================= */}
      <section
        data-testid="the-project-section"
        className="bg-[#1A1A1A] text-[#F9F9F6]"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-6">
              <p className="eyebrow text-[#A8A8A0]">02 / The Project</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
                The Project.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-6 self-end">
              <p className="text-base md:text-lg text-[#E0D8CD] leading-relaxed max-w-xl">
                A vertically integrated leather processing and manufacturing platform based in Lobatse, within the SEZA industrial framework.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-4">
              <p className="eyebrow text-[#E5C4A1]">Structure</p>
              <p className="font-serif text-3xl md:text-4xl tracking-tight mt-4 leading-tight">
                SPV
              </p>
              <p className="font-serif text-2xl text-[#E5C4A1] italic mt-2">
                LRG Leather Botswana
              </p>
            </div>
            <div className="md:col-span-8">
              <p className="eyebrow text-[#E5C4A1]">Scope</p>
              <ul className="mt-4 border-t border-[#333330]">
                {PROJECT_SCOPE.map((s) => (
                  <li
                    key={s}
                    data-testid={`project-scope-${PROJECT_SCOPE.indexOf(s)}`}
                    className="flex items-center gap-4 py-5 border-b border-[#333330]"
                  >
                    <span className="font-serif text-[#E5C4A1] text-sm w-6">
                      {String(PROJECT_SCOPE.indexOf(s) + 1).padStart(2, "0")}
                    </span>
                    <span className="font-serif text-xl md:text-2xl tracking-tight leading-snug">
                      {s}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 4. CURRENT STATUS ================= */}
      <section
        data-testid="current-status-section"
        className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32"
      >
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow">03 / Current Status</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
              Current Status.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-sm text-[#666666] leading-relaxed">
              Factual position, as of current structuring phase.
            </p>
          </div>
        </div>

        <div className="border-t border-[#1A1A1A]">
          {CURRENT_STATUS.map((s, i) => (
            <div
              key={s}
              data-testid={`status-row-${i}`}
              className="grid grid-cols-12 gap-6 py-6 md:py-7 border-b border-[#E5E5E0] items-center"
            >
              <span className="col-span-2 md:col-span-1 font-serif text-xl text-[#5C2018]">
                0{i + 1}
              </span>
              <p className="col-span-10 md:col-span-10 font-serif text-xl md:text-2xl tracking-tight leading-snug text-[#1A1A1A]">
                {s}
              </p>
              <div className="col-span-12 md:col-span-1 flex md:justify-end">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#5C2018] border border-[#5C2018] px-2 py-1">
                  Active
                </span>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 font-serif text-lg md:text-xl tracking-tight text-[#1A1A1A] max-w-3xl leading-snug">
          <span className="eyebrow block mb-3">Focus</span>
          Aligning supply, processing capability, and export channels into a single operating platform.
        </p>

        {/* Supply Resilience by Design — FMD narrative */}
        <div data-testid="supply-resilience-block" className="mt-16 border-l-2 border-[#5C2018] pl-6 md:pl-8 max-w-4xl">
          <p className="eyebrow text-[#5C2018]">Supply Resilience by Design</p>
          <p className="mt-4 text-base md:text-lg text-[#1A1A1A] leading-relaxed">
            Botswana&rsquo;s beef sector is currently navigating a foot-and-mouth disease containment cycle, with BMC operations suspended as of July 2026. The LRG Leather Botswana platform is structured to be resilient to precisely this kind of single-source risk.
          </p>
          <p className="mt-4 text-base md:text-lg text-[#1A1A1A] leading-relaxed">
            Phase 1 hide sourcing is designed as a multi-country SADC intake. Botswana as the anchor, with parallel supply agreements under evaluation across Namibia, South Africa, and Zimbabwe. This is not a workaround. It is the intended structure. Regional zoning, veterinary compliance, and hide-quality specification (bull-to-cow ratio, weight class, cure method) will be documented at the SPV level before Phase 1 commissioning.
          </p>
        </div>
      </section>

      {/* ================= 5. WHY THIS VERSION WORKS ================= */}
      <section
        data-testid="why-this-version-section"
        className="bg-[#F9F9F6] border-t border-[#E5E5E0]"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-7">
              <p className="eyebrow">04 / Why This Version Works</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
                Why This Version Works.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 border-l border-t border-[#1A1A1A]">
            {WHY_THIS_VERSION.map((b, i) => (
              <div
                key={b}
                data-testid={`why-version-${i}`}
                className="border-r border-b border-[#1A1A1A] p-8 md:p-10 bg-[#F9F9F6] hover:bg-white transition-colors"
              >
                <div className="flex items-start gap-5">
                  <span className="font-serif text-[#5C2018] text-2xl mt-1">
                    0{i + 1}
                  </span>
                  <p className="font-serif text-xl md:text-2xl tracking-tight leading-snug text-[#1A1A1A]">
                    {b}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 6. WHY BOTSWANA ================= */}
      <section
        data-testid="why-botswana-section"
        className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32"
      >
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow">05 / Why Botswana</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
              Why Botswana.
            </h2>
          </div>
        </div>

        <ul className="border-t border-[#1A1A1A]">
          {WHY_BOTSWANA.map((b, i) => (
            <li
              key={b}
              data-testid={`why-botswana-${i}`}
              className="grid grid-cols-12 gap-6 py-6 md:py-7 border-b border-[#E5E5E0] items-center group"
            >
              <span className="col-span-2 md:col-span-1 font-serif text-xl text-[#5C2018]">
                0{i + 1}
              </span>
              <p className="col-span-10 md:col-span-10 font-serif text-xl md:text-2xl tracking-tight leading-snug">
                {b}
              </p>
              <div className="col-span-12 md:col-span-1 flex md:justify-end">
                <ArrowUpRight size={18} className="text-[#666666] group-hover:text-[#5C2018] transition-colors" />
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* ================= 6. ENVIRONMENTAL & SOCIAL DESIGN ================= */}
      <section
        data-testid="esg-section"
        className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 border-t border-[#E5E5E0]"
      >
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow">06 / Environmental &amp; Social Design</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
              Environmental &amp; Social Design.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-sm text-[#1A1A1A] leading-relaxed">
              LRG Leather Botswana is being designed to Leather Working Group (LWG) audit-readiness from Phase 1. Environmental engineering decisions will be locked with the technical partner at design-freeze. Several remain open by intent, because they are downstream of end-market specification.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 border-l border-t border-[#1A1A1A]">
          {[
            {
              n: "01",
              status: "Under Evaluation",
              title: "Tanning Chemistry",
              body: "Chrome-free / wet-white and vegetable-tanning chemistries are both under active review. Final chemistry selection will follow anchor off-take specification. Automotive and mid-luxury end-markets typically specify wet-white or chrome-managed processes; high-luxury European off-take typically specifies vegetable-tanned Tuscan tradition. The plant is being designed to support the chemistry the off-take requires.",
            },
            {
              n: "02",
              status: "Non-Negotiable",
              title: "Water &amp; Effluent",
              body: "Site design assumes on-plot effluent treatment, sludge management, and water-recycling infrastructure to Italian tannery-cluster standards. Italprogetti&rsquo;s environmental division designs the wastewater, sludge, and gas-treatment systems for the Tuscan leather cluster; the same specification standard applies here.",
            },
            {
              n: "03",
              status: "By Design",
              title: "Traceability",
              body: "Multi-country SADC hide intake paired with hide-level traceability documentation. Country of origin, veterinary zone, cure method, and weight class captured at intake and preserved through to finished-good level.",
            },
            {
              n: "04",
              status: "Targeted",
              title: "Certifications",
              body: "LWG (Leather Working Group); ZDHC alignment on chemistry; SEZA environmental compliance; Botswana Department of Environmental Affairs sign-off.",
            },
          ].map((c) => (
            <div key={c.n} data-testid={`esg-${c.n}`} className="border-r border-b border-[#1A1A1A] p-8 md:p-10 bg-[#F9F9F6] hover:bg-white transition-colors">
              <div className="flex items-center justify-between">
                <span className="font-serif text-2xl text-[#5C2018]">{c.n}</span>
                <span className="text-[10px] uppercase tracking-[0.22em] border border-[#1A1A1A] px-2 py-1">
                  {c.status}
                </span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight mt-8 leading-tight">{c.title}</h3>
              <p className="mt-4 text-sm md:text-base text-[#1A1A1A] leading-relaxed" dangerouslySetInnerHTML={{ __html: c.body }} />
            </div>
          ))}
        </div>
      </section>

      {/* ================= 7. INVESTMENT OPPORTUNITY ================= */}
      <section
        data-testid="investment-opportunity-section"
        className="bg-[#1A1A1A] text-[#F9F9F6]"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-10">
            <div className="col-span-12 md:col-span-7">
              <p className="eyebrow text-[#A8A8A0]">07 / Investment Opportunity</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
                Investment Opportunity.
              </h2>
            </div>
          </div>

          {/* Anchor Round callout — explicit capital ask */}
          <div data-testid="anchor-round-callout" className="mb-10 border border-[#E5C4A1] p-6 md:p-8 bg-[#5C2018]/20">
            <p className="eyebrow text-[#E5C4A1]">Anchor Round</p>
            <p className="font-serif text-3xl md:text-4xl tracking-tight mt-3 leading-tight text-[#F9F9F6]">
              USD 5&ndash;15M
            </p>
            <p className="mt-4 text-sm md:text-base text-[#E0D8CD] leading-relaxed max-w-3xl">
              Phase 1 site development, technical partnership engagement, and SPV-level pre-construction workstreams. Instrument, tranching, and drawdown schedule to be structured with anchor investor. Blended-finance and DFI participation actively welcomed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-[#333330] border border-[#333330]">
            <div data-testid="inv-platform" className="md:col-span-6 bg-[#1A1A1A] p-8 md:p-10">
              <p className="eyebrow text-[#E5C4A1]">Platform</p>
              <p className="font-serif text-2xl md:text-3xl tracking-tight mt-5 leading-snug">
                Mid-scale industrial manufacturing platform combining tanning and finished goods production.
              </p>
            </div>

            <div data-testid="inv-capital" className="md:col-span-6 bg-[#1A1A1A] p-8 md:p-10">
              <p className="eyebrow text-[#E5C4A1]">Investment Structure</p>
              <p className="font-serif text-xl md:text-2xl tracking-tight mt-5 leading-snug">
                Anchor round into a Botswana-domiciled SPV (&ldquo;LRG Leather Botswana&rdquo;) under SEZA licensing. Instrument, tranching, minority-partner participation, and exit design to be structured with anchor investor. Governance, reporting, and DFI-compliance frameworks under development in parallel with SEZA aftercare.
              </p>
            </div>

            <div data-testid="inv-revenue" className="md:col-span-6 bg-[#1A1A1A] p-8 md:p-10">
              <p className="eyebrow text-[#E5C4A1]">Revenue Drivers</p>
              <ul className="mt-5 space-y-3">
                {["Leather processing", "Finished goods export", "Value-added manufacturing"].map((d) => (
                  <li key={d} className="font-serif text-xl md:text-2xl tracking-tight flex items-center gap-3">
                    <span className="h-px w-6 bg-[#E5C4A1]" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="inv-current-phase" className="md:col-span-6 bg-[#1A1A1A] p-8 md:p-10">
              <p className="eyebrow text-[#E5C4A1]">Current Phase</p>
              <p className="font-serif text-2xl md:text-3xl tracking-tight mt-5 leading-snug">
                Initial phase structuring. Capital, technical, and strategic partners being aligned.
              </p>
            </div>
          </div>

          <div className="mt-10">
            <Link
              to="/investment"
              data-testid="inv-cta"
              className="inline-flex items-center gap-3 px-7 py-4 bg-[#F9F9F6] text-[#1A1A1A] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#5C2018] hover:text-[#F9F9F6] transition-colors"
            >
              Investment Structure
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ================= 8. PARTNERSHIP MODEL ================= */}
      <section
        data-testid="partnership-model-section"
        className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32"
      >
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow">08 / Partnership Model</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
              Partnership Model.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-[#1A1A1A]">
          {PARTNERSHIP.map((p) => (
            <div
              key={p.code}
              data-testid={`partnership-${p.code.toLowerCase()}`}
              className="border-r border-b border-[#1A1A1A] p-8 md:p-10 bg-[#F9F9F6] hover:bg-white transition-colors flex flex-col"
            >
              <span className="font-serif text-[#5C2018] text-sm tracking-[0.05em] border border-[#5C2018] px-3 py-1 self-start">
                {p.code}
              </span>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight mt-8 leading-tight">
                {p.name}
              </h3>
              <p className="text-sm text-[#1A1A1A] mt-4 leading-relaxed">{p.role}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ================= 9. TECHNICAL PARTNERS ================= */}
      <section
        data-testid="technical-partners-section"
        className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 border-t border-[#E5E5E0]"
      >
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow">09 / Technical Partners</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
              Technical Partners.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-sm text-[#1A1A1A] leading-relaxed">
              Engaging Italian engineering at design stage. A deliberate choice to align the platform with premium end-market specification from day one.
            </p>
          </div>
        </div>

        <div className="border border-[#1A1A1A] bg-white grid grid-cols-12">
          <div className="col-span-12 md:col-span-4 bg-[#1A1A1A] text-[#F9F9F6] p-8 md:p-10 lg:p-12 flex flex-col justify-between">
            <div>
              <span className="font-serif text-[#E5C4A1] text-sm tracking-[0.05em] border border-[#E5C4A1] px-3 py-1 inline-block">
                ITALPROGETTI
              </span>
              <h3 className="font-serif text-3xl md:text-4xl tracking-tight mt-8 leading-tight">
                Italprogetti S.p.A.
              </h3>
              <p className="eyebrow text-[#A8A8A0] mt-3">
                Tuscan Tannery Engineering
              </p>
            </div>
            <div className="mt-10 space-y-3 text-sm text-[#C7C7BF]">
              <p><span className="text-[#E5C4A1]">Founded</span> · 1977</p>
              <p><span className="text-[#E5C4A1]">Location</span> · San Romano, Pisa, Italy</p>
              <p><span className="text-[#E5C4A1]">Export markets</span> · 50-90+</p>
              <p><span className="text-[#E5C4A1]">Membership</span> · Leather Working Group</p>
              <p><span className="text-[#E5C4A1]">Engagement stage</span> · Scoping (Underway)</p>
            </div>
          </div>
          <div className="col-span-12 md:col-span-8 p-8 md:p-10 lg:p-12">
            <p className="eyebrow text-[#5C2018]">Phase 1 Engineering &amp; Plant Design</p>
            <p className="font-serif text-xl md:text-2xl tracking-tight mt-4 leading-snug">
              Phase 1 engineering and plant design is being scoped in engagement with Italprogetti S.p.A., a Tuscan tannery-engineering firm and one of the reference global suppliers of tannery drum, water-treatment, and process-automation systems.
            </p>
            <p className="mt-6 text-base text-[#1A1A1A] leading-relaxed max-w-3xl">
              The Tuscan leather cluster (Santa Croce sull&rsquo;Arno / Ponte a Egola) is the technical benchmark for luxury and automotive-grade leather globally. Italprogetti&rsquo;s environmental division designs the wastewater, sludge, and gas-treatment systems for the cluster. The same specification standard is intended here.
            </p>
            <p className="mt-6 text-xs text-[#666666] max-w-3xl">
              Engagement stage is scoping. No signed contract is claimed. Status is reflected as &ldquo;Underway&rdquo; in the Current Progress table.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.22em] text-[#666666]">
              <span className="border border-[#E5E5E0] px-3 py-1">Tannery drums</span>
              <span className="border border-[#E5E5E0] px-3 py-1">Water treatment</span>
              <span className="border border-[#E5E5E0] px-3 py-1">Process automation</span>
              <span className="border border-[#E5E5E0] px-3 py-1">Sludge management</span>
              <span className="border border-[#E5E5E0] px-3 py-1">Gas treatment</span>
            </div>
          </div>
        </div>

        <p className="mt-8 text-xs text-[#666666] max-w-3xl">
          Additional Italian and European technical partners (chemistry suppliers, LWG auditors, luxury off-take contacts) will be added as engagements progress.
        </p>
      </section>

      {/* ================= PROJECT SPONSOR ================= */}
      <section data-testid="sponsor-section" className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 border-t border-[#E5E5E0]">
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow">Project Sponsor</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
              Who Is Behind This.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-sm text-[#1A1A1A] leading-relaxed">
              Counterparties will ask who is structuring this. Named principal, citizen-owned enterprise, direct line.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-0 border border-[#1A1A1A]">
          <div className="col-span-12 md:col-span-3 bg-[#1A1A1A] text-[#F9F9F6] p-8 md:p-10 flex flex-col items-center justify-center">
            <div className="w-24 h-24 border border-[#E5C4A1] flex items-center justify-center font-serif text-3xl text-[#E5C4A1]">
              LR
            </div>
            <p className="font-serif text-lg mt-6 tracking-tight text-center">Lefika Ragontse</p>
            <p className="eyebrow text-[#A8A8A0] mt-2 text-center">Founder &amp; Developer</p>
          </div>
          <div className="col-span-12 md:col-span-9 p-8 md:p-10 lg:p-12 bg-[#F9F9F6]">
            <p className="eyebrow text-[#5C2018]">LRG Holdings · Citizen-Owned Enterprise</p>
            <h3 className="font-serif text-2xl md:text-3xl tracking-tight mt-4 leading-tight">
              Lefika Ragontse is the founder of LRG Holdings, the project developer and operating entity behind the Botswana Leather Park.
            </h3>
            <p className="mt-5 text-sm md:text-base text-[#1A1A1A] leading-relaxed max-w-3xl">
              LRG Holdings is a citizen-owned enterprise focused on building globally competitive platforms from African supply chains. The principal carries experience across international trade, manufacturing partnerships and resource-based industries.
            </p>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-[#E5E5E0] pt-6">
              {[
                ["Company", "LRG Holdings"],
                ["Status", "Citizen-Owned"],
                ["Location", "Gaborone, Botswana"],
                ["Role", "Developer & Operator"],
              ].map(([k, v]) => (
                <div key={k}>
                  <p className="eyebrow">{k}</p>
                  <p className="font-serif text-base md:text-lg mt-2">{v}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="mailto:lefikaragontse@lrgholdings.com"
                data-testid="sponsor-email"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#5C2018] text-[#F9F9F6] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A] transition-colors"
              >
                lefikaragontse@lrgholdings.com
              </a>
              <a
                href="tel:+14105370871"
                data-testid="sponsor-phone"
                className="inline-flex items-center gap-2 px-5 py-3 border border-[#1A1A1A] text-[#1A1A1A] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A] hover:text-[#F9F9F6] transition-colors"
              >
                +1 (410) 537-0871
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CURRENT PROGRESS — TRANSPARENCY ================= */}
      <section data-testid="current-progress-section" className="bg-[#1A1A1A] text-[#F9F9F6]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-6">
              <p className="eyebrow text-[#A8A8A0]">Transparency</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-5">
                Current progress.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
              <p className="text-sm text-[#C7C7BF] leading-relaxed">
                We distinguish between what is confirmed and what is proposed. No overstatement. Status as of current structuring phase.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-[#333330]">
            {[
              ["Granted", "SEZA Investor Licence — No. SEZBW00029 (16 Mar 2026, 50-year validity)", "G"],
              ["Underway", "Land allocation discussions in Lobatse SEZ", "U"],
              ["Underway", "Technical partner engagement with Italian manufacturers", "U"],
              ["Underway", "Financial structuring and investment documentation", "U"],
              ["Next Required", "BPC grid connection agreement for solar infrastructure", "N"],
              ["Next Required", "BERA licensing approval for IPP operations", "N"],
            ].map(([status, body], i) => (
              <div key={i} data-testid={`progress-item-${i}`} className="border-r border-b border-[#333330] p-6 md:p-8">
                <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border inline-block ${
                  status === "Granted" ? "border-[#E5C4A1] text-[#E5C4A1] bg-[#E5C4A1]/10" :
                  status === "Underway" ? "border-[#F9F9F6] text-[#F9F9F6]" :
                  status === "Proposed" ? "border-[#F9F9F6] text-[#F9F9F6]" :
                  "border-[#A8A8A0] text-[#A8A8A0]"
                }`}>
                  {status}
                </span>
                <p className="font-serif text-xl md:text-2xl tracking-tight leading-snug mt-6">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 9. EXECUTION PRIORITIES ================= */}
      <section
        data-testid="execution-priorities-section"
        className="bg-[#F9F9F6] border-t border-[#E5E5E0]"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-7">
              <p className="eyebrow">10 / Execution Priorities</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
                Execution Priorities.
              </h2>
            </div>
          </div>

          <ul className="border-t border-[#1A1A1A]">
            {PRIORITIES.map((p, i) => (
              <li
                key={p}
                data-testid={`priority-${i}`}
                className="grid grid-cols-12 gap-6 py-6 md:py-7 border-b border-[#E5E5E0] items-center"
              >
                <span className="col-span-2 md:col-span-1 font-serif text-xl text-[#5C2018]">
                  0{i + 1}
                </span>
                <p className="col-span-10 md:col-span-10 font-serif text-xl md:text-2xl tracking-tight leading-snug">
                  {p}
                </p>
                <div className="col-span-12 md:col-span-1 flex md:justify-end">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#666666]">
                    In progress
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ================= 10. ENGAGE WITH THE PLATFORM ================= */}
      <section
        data-testid="engage-section"
        className="bg-[#5C2018] text-[#F9F9F6]"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-8">
              <p className="eyebrow text-[#E5C4A1]">11 / Engage</p>
              <h2 className="font-serif text-5xl md:text-6xl lg:text-[80px] tracking-tight leading-[0.98] mt-5">
                Engage With the Platform.
              </h2>
            </div>
          </div>

          <p className="text-base md:text-lg text-[#E0D8CD] max-w-2xl">
            We are currently engaging:
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 border-l border-t border-[#7A3A2E]">
            {COUNTERPARTIES.map((c, i) => (
              <div
                key={c}
                data-testid={`engage-counterparty-${i}`}
                className="border-r border-b border-[#7A3A2E] p-8 md:p-10 bg-[#5C2018]"
              >
                <span className="font-serif text-xl text-[#E5C4A1]">
                  0{i + 1}
                </span>
                <p className="font-serif text-2xl md:text-3xl tracking-tight mt-8 leading-tight">
                  {c}
                </p>
              </div>
            ))}
          </div>

          <p
            data-testid="engage-final-line"
            className="mt-12 font-serif text-2xl md:text-3xl lg:text-4xl tracking-tight leading-snug max-w-4xl font-semibold"
          >
            Serious counterparties are invited to engage during the current structuring phase.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="engage-cta-contact"
              className="inline-flex items-center gap-3 px-7 py-4 bg-[#F9F9F6] text-[#1A1A1A] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A] hover:text-[#F9F9F6] transition-colors"
            >
              Contact LRG Holdings
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/partner"
              data-testid="engage-cta-partner"
              className="inline-flex items-center gap-3 px-7 py-4 border border-[#F9F9F6]/50 text-[#F9F9F6] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#F9F9F6] hover:text-[#5C2018] transition-colors"
            >
              Partner Track
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
